<template>
  <v-dialog v-model="isOpen" max-width="1000px" scrollable>
    <v-card color="white" class="search-modal-card d-flex flex-column" height="90vh">
      <!-- Header -->
      <v-card-title class="pa-4 black-text d-flex flex-row justify-space-between" style="flex-wrap: nowrap;">
        <h4 class="text-lg font-semibold mb-0" style="flex: 1 1 auto;">
          {{$t('conditions_covered.title')}}
        </h4>
        <v-btn icon @click="close" style="flex: 0 0 auto; align-self: flex-start; margin-top: 0;">
          <v-icon color="black">{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider color="lightgrey" />

      <!-- Search Bar -->
      <v-card-text class="pa-4 flex-grow-0">
        <v-text-field
          v-model="searchQuery"
          :placeholder="$t('conditions_covered.search_placeholder')"
          :prepend-inner-icon="icons.mdiMagnify"
          outlined
          dense
          hide-details
          class="mb-4 search-modal-text-field"
          light
        />

        <!-- Filters -->
        <div>
          <div class="search-modal-filter-bar mb-2">
            <span class="search-modal-filter-label">
              <v-icon color="rgb(55 65 81 / 1)" class="mr-1.5 text-xl">
                {{ icons.mdiFilterOutline }}
              </v-icon>
                {{$t('conditions_covered.filter_by')}}
            </span>

            <v-chip
              :class="['search-modal-filter-chip', { 'search-modal-filter-chip--active': showPatientTypeFilter }]"
              small
              outlined
              @click="toggleFilter('patientType')"
            >
              {{$t('conditions_covered.patient_type')}}
            </v-chip>

            <v-chip
              :class="['search-modal-filter-chip', { 'search-modal-filter-chip--active': showDiseaseCategoryFilter }]"
              small
              outlined
              @click="toggleFilter('diseaseCategory')"
            >
              {{$t('conditions_covered.disease_category')}}
            </v-chip>

            <v-btn
              v-if="hasActiveFilters"
              small
              text
              rounded-pill
              id="search-modal-filter-clear-btn"
              @click="clearAllFilters"
            >
              <span class="search-modal-filter-clear-label">
                <v-icon left>
                  {{ icons.mdiCloseCircleOutline }}
                </v-icon>
                  {{$t('conditions_covered.clear_all_filters')}}
              </span>
            </v-btn>
          </div>

          <!-- Patient Type Filter -->
          <div v-if="showPatientTypeFilter">
            <div class="mb-2 d-flex align-center">
              <span class="black-text" style="font-size: 1rem;">
                 {{ filterSelectionText(selectedPatientTypes, 'patient_type') }}
              </span>
              <div style="flex: 1 1 auto" />
              <v-btn
                v-if="selectedPatientTypes.length > 0"
                small
                text
                data-testid="clear-patient-type-btn"
                class="search-modal-clear-btn"
                @click="selectedPatientTypes = []"
              >
                {{$t('conditions_covered.clear_all')}}
              </v-btn>
            </div>

            <div class="d-flex flex-wrap mb-2">
              <v-btn
                v-for="typeObj in patientTypeOptions"
                :key="typeObj.key"
                small
                rounded-pill
                class="mr-2 mb-2 search-modal-type-btn"
                :id="selectedPatientTypes.includes(typeObj.key) ? 'search-modal-type-btn--active' : null"
                :class="{ 'search-modal-type-btn--active': selectedPatientTypes.includes(typeObj.key) }"
                @click="toggleSelection(selectedPatientTypes, typeObj.key)"
              >
                {{trackTitle(typeObj)}}
              </v-btn>
            </div>
          </div>

          <!-- Disease Category Filter -->
          <div v-if="showDiseaseCategoryFilter">
            <div class="mb-2 d-flex align-center">
              <span class="black-text" style="font-size: 1rem;">
                   {{ filterSelectionText(selectedDiseaseCategories, 'disease_category') }}
              </span>
              <div style="flex: 1 1 auto" />
              <v-btn
                v-if="selectedDiseaseCategories.length > 0"
                small
                text
                class="clear-disease-category-btn search-modal-clear-btn"
                data-testid="clear-disease-category-btn"
                @click="selectedDiseaseCategories = []"
              >
                  {{$t('conditions_covered.clear_all')}}
              </v-btn>
            </div>

            <div class="search-modal-category-filter-scroll d-flex flex-wrap mb-2">
              <v-btn
                v-for="cat in diseaseCategoryOptions.slice().sort((a, b) => $t(`condition_category.${a}.title`).localeCompare($t(`condition_category.${b}.title`)))"
                :key="cat"
                small
                rounded-pill
                class="mr-2 mb-2 search-modal-type-btn"
                :id="selectedDiseaseCategories.includes(cat) ? 'search-modal-type-btn--active' : null"
                @click="toggleSelection(selectedDiseaseCategories, cat)"
              >
                {{$t(`condition_category.${cat}.title`)}}
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider color="lightgrey" />

      <!-- Results -->
      <v-card-text class="pa-4" style="flex: 1 1 0; min-height: 0; overflow-y: auto">
        <div v-if="filteredConditions.length > 0">
          <v-card
            v-for="(condition, index) in filteredConditions"
            :key="index"
            class="mb-3"
          >
            <v-card-title
              class="pa-3 grey lighten-3 d-flex justify-space-between"
              style="border-top-left-radius: 10px; border-top-right-radius: 10px; padding: 12px 16px; margin: 0"
            >
              <span class="search-modal-condition-title">
                {{$t(condition.titleToken)}}
              </span>
              <v-chip
                small
                :color="condition.categoryColor"
                :text-color="condition.categoryTextColor"
                class="search-modal-condition-category-chip"
              >
                {{$t(condition.categoryToken)}}
              </v-chip>
            </v-card-title>
            <v-card-text class="pa-3" style="background-color: white;">
              <div class="patient-types-container">
                <div class="patient-types-wrapper">
                  <div v-if="condition.patientTypes && condition.patientTypes.length">
                    <div class="patient-types-grid">
                      <div class="patient-types-column">
                        <span
                          v-for="pt in condition.patientTypes.filter((_, i) => i % 2 === 0)"
                          :key="'col1-' + pt.key"
                          class="patient-type-text"
                        >
                          {{trackTitle(pt)}}
                        </span>
                      </div>

                      <div class="patient-types-column">
                        <span
                          v-for="pt in condition.patientTypes.filter((_, i) => i % 2 === 1)"
                          :key="'col2-' + pt.key"
                          class="patient-type-text"
                        >
                          {{trackTitle(pt)}}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <div v-else class="text-center py-8">
            <span class="black-text">{{$t('conditions_covered.no_results')}}</span>
        </div>
      </v-card-text>

      <v-divider color="lightgrey" />

      <v-card-actions class="pa-4 grey lighten-3 justify-center">
        <v-btn id="suggest-assessment-btn" @click="openSuggestionForm">
          <v-icon color="white">
            {{ icons.mdiInformationOutline }}
          </v-icon>
          <span class="text-center" style="color: white; padding-left: 2px;">
              {{$t('conditions_covered.suggest_assessment')}}
          </span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import icons from '@/mixins/icons'
import { getCategoryColors } from '../../utils/conditionsCoveredColors.js'
import { filterAndSearchConditions, conditionTrack, conditionTrackTitle } from '../../utils/conditionsCoveredUtils'
import { conditions as mrxConditions } from '@/utils/mrxEngine'
import { mapState } from 'pinia'
import { useTrackStore } from '@/stores/track'
import { createDebouncedTracker } from '@/plugins/analytics/helpers'

export default {
  name: 'SearchModal',
  mixins: [icons],

  props: {
    show: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      searchQuery: '',
      conditions: [],
      showPatientTypeFilter: false,
      showDiseaseCategoryFilter: false,
      patientTypeOptions: [],
      selectedPatientTypes: [],
      diseaseCategoryOptions: [],
      selectedDiseaseCategories: []
    }
  },

  computed: {
    ...mapState(useTrackStore, { allTracks: 'all' }),
    isOpen: {
      get() {
        return this.show
      },
      set(value) {
        if (!value) this.close()
      }
    },
    hasActiveFilters() {
      return (
        this.showPatientTypeFilter ||
        this.selectedPatientTypes.length > 0 ||
        this.showDiseaseCategoryFilter ||
        this.selectedDiseaseCategories.length > 0
      )
    },
    filteredConditions() {
      return filterAndSearchConditions({
        conditions: this.conditions.filter(condition => condition.patientTypes && condition.patientTypes.length).map(condition => ({
          ...condition,
          title: this.$t(condition.titleToken),
          category: condition.categoryToken ? this.$t(condition.categoryToken) : condition.category,
          patientTypes: condition.patientTypes.map(track => ({
            ...track,
            title: conditionTrackTitle(track, token => this.$t(token))
          }))
        })),
        searchQuery: this.searchQuery,
        selectedPatientTypes: this.selectedPatientTypes,
        selectedDiseaseCategories: this.selectedDiseaseCategories,
      })
    }
  },

  watch: {
    allTracks(newTracks) {
      if (newTracks) {
        this.fetchConditionsFromBackend()
      }
    },
    searchQuery(newValue, oldValue) {
      if (newValue && newValue.length > 0) {
        this.trackSearchResults(this.$analyticsEvents.MULTISELECT_SEARCH_USED, {
          component: 'SearchModal',
          panel_name: 'conditions_covered',
          results_count: this.filteredConditions.length
        })
      }
    },
    selectedPatientTypes(newValue) {
      if (newValue && newValue.length > 0) {
        this.$track(this.$analyticsEvents.CLICK_EVENT, {
          component: 'SearchModal',
          action: 'filter_patient_type',
          selected_types: newValue,
          count: newValue.length
        })
      }
    },
    selectedDiseaseCategories(newValue) {
      if (newValue && newValue.length > 0) {
        this.$track(this.$analyticsEvents.CLICK_EVENT, {
          component: 'SearchModal',
          action: 'filter_disease_category',
          selected_categories: newValue,
          count: newValue.length
        })
      }
    }
  },

  created() {
    this.trackSearchResults = createDebouncedTracker((event, props) => {
      this.$track(event, props)
      if (props.results_count === 0) {
        this.$track(this.$analyticsEvents.SEARCH_NO_RESULTS, props)
      }
    }, 600)
  },

  mounted() {
    this.fetchConditionsFromBackend()
  },

  methods: {
    trackTitle(track) {
      return conditionTrackTitle(track, token => this.$t(token))
    },
    getCategoryColors,
    close() {
      this.clearAllFilters();
      this.$emit('close');
    },
    openSuggestionForm() {
      this.$emit('open-suggestion-form')
    },
    async fetchConditionsFromBackend() {
      try {
        const data = await mrxConditions()
        const tracks = this.allTracks || []

        this.patientTypeOptions = tracks.map(conditionTrack)

        const categoriesSet = new Set()
        data.forEach(cond => (cond.categories || []).forEach(cat => categoriesSet.add(cat)))
        this.diseaseCategoryOptions = [...categoriesSet]

        this.conditions = data.map(cond => {
          const diseaseKey = cond.name
          const titleToken = `assessment.${diseaseKey}.title`

          let categoryKey = ''
          let categoryToken = ''
          let categoryColor = '#60A5FA'
          let categoryTextColor = '#FFFFFF'

          if (cond.categories && cond.categories.length > 0) {
            categoryKey = cond.categories[0]
            categoryToken = `condition_category.${categoryKey}.title`
            const colors = this.getCategoryColors(categoryKey)
            categoryColor = colors.bg
            categoryTextColor = colors.text
          }

          const displayPatientTypes = tracks
            .filter(track => (track.diseases || []).some(d => d.name === diseaseKey))
            .map(conditionTrack)
          return {
            title: cond.title || diseaseKey,
            titleToken,
            categoryToken,
            categoryKey, 
            categoryColor,
            categoryTextColor,
            patientTypes: displayPatientTypes
          }
        })
      } catch (error) {
        this.$sentry.captureException(error)
        console.error('Error fetching conditions:', error)
      }
    },
    toggleFilter(filterType) {
      if (filterType === 'patientType') {
        this.showPatientTypeFilter = !this.showPatientTypeFilter;
        if (this.showPatientTypeFilter) this.showDiseaseCategoryFilter = false;
      } else {
        this.showDiseaseCategoryFilter = !this.showDiseaseCategoryFilter;
        if (this.showDiseaseCategoryFilter) this.showPatientTypeFilter = false;
      }
    },
    toggleSelection(array, value) {
      const idx = array.indexOf(value)
      if (idx === -1) array.push(value)
      else array.splice(idx, 1)
    },
    filterSelectionText(arr, type = 'patient_type') {
      const defaultKey = type === 'patient_type'
        ? 'conditions_covered.select_patient_type'
        : 'conditions_covered.select_disease_category';

      if (!arr.length) return this.$t(defaultKey);

      const key = arr.length === 1
        ? `conditions_covered.selected_singular_${type}`
        : `conditions_covered.selected_plural_${type}`;

      return this.$t(key, { count: arr.length });
    },
    clearAllFilters() {
      this.selectedPatientTypes = []
      this.selectedDiseaseCategories = []
      this.searchQuery = ''
      this.showPatientTypeFilter = false
      this.showDiseaseCategoryFilter = false
    }
  }
}
</script>

<style scoped>
.v-btn.search-modal-type-btn,
.v-btn.search-modal-type-btn.v-btn,
.v-btn.search-modal-type-btn.v-btn.v-btn--has-bg {
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
}

#search-modal-type-btn--active {
  background-color: #2563eb;
  color: white;
  border-radius: 20px;
}
.v-chip.search-modal-filter-chip.v-chip--outlined {
  background-color: #e5e7eb !important;
  color: rgb(55 65 81 / 1) !important;
  font-size: 1rem;
}
.v-chip.search-modal-filter-chip--active.v-chip--outlined {
  background-color: #2563eb !important;
  color: white !important;
}
.v-btn.search-modal-clear-btn {
  color: #2563eb;
  border-radius: 20px;
}
.black-text {
  color: black;
}
#suggest-assessment-btn {
  width: 50%;
  background-color: #3b82f6;
}
.search-modal-filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  width: 100%;
}
.search-modal-filter-label {
  display: flex;
  align-items: center;
  font-size: 1.08rem;
  color: rgb(55 65 81 / 1);
  margin-right: 2px;
}
#search-modal-filter-clear-btn {
  color: #2563eb;
  background-color: #e0f2fe;
  margin-left: auto;
  border-radius: 20px;
}
.search-modal-filter-clear-label {
  display: flex;
  font-size: 0.9rem;
}
.search-modal-condition-category-chip {
  font-size: 1rem;
  font-weight: 600;
}
.search-modal-condition-title {
  font-weight: bold;
  font-size: 1.06rem;
  color: black;
}
.search-modal-category-filter-scroll {
  max-height: 100px;
  min-height: 25px;
  overflow-y: auto;
  overflow-x: hidden;
}
.patient-types-container {
  display: flex;
  gap: 32px;
  padding-top: 10px;
  padding-bottom: 10px;
}
.patient-types-wrapper {
  flex: 1;
  min-width: 0;
}
.patient-types-grid {
  display: flex;
}
.patient-types-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.patient-type-text {
  font-size: 1.08rem;
  font-weight: 500;
  color: #222;
}

@media (max-width: 400px) {
  #search-modal-filter-clear-btn {
    margin-left: 0;
    width: 100%;
    justify-content: center;
    text-align: center;
    display: flex;
    align-items: center;
  }
  .search-modal-condition-category-chip {
    display: block;
    width: 100%;
    text-align: center;
  }
  .search-modal-category-filter-scroll {
    max-height: 35px;
    min-height: 35px;
  }
  #suggest-assessment-btn {
    width: 100%;
    background-color: #3b82f6;
    font-size: 0.7rem;
  }
}
@media (max-width: 800px) {
  #suggest-assessment-btn {
    font-size: 0.7rem;
    font-weight: bold;
    width: 100%;
    background-color: #3b82f6;
  }
}
</style>
